import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { OfferProvider } from "../../../servicing/models/receivedOffersModels";

interface OfferProviderCardProps {
  provider?: OfferProvider;
}

const OfferProviderCard: React.FC<OfferProviderCardProps> = ({ provider }) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  if (!provider) return null;

  const logoSize = isPhone ? 32 : 48;
  const locationText = [provider.city, provider.countryName].filter(Boolean).join(", ");

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radius,
        padding: isPhone ? "12px 14px" : "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: isPhone ? 1 : 1.5,
      }}
    >
      {/* Section label */}
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1px",
          color: dt.textMuted,
          fontFamily: dt.font,
          mb: "4px",
        }}
      >
        {translate("offerDetail.providerInfo")}
      </Box>

      {/* Logo + name */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {provider.logoUrl ? (
          <Box
            component="img"
            src={provider.logoUrl}
            alt={provider.name}
            sx={{
              width: logoSize,
              height: logoSize,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
              border: `2px solid ${dt.cardBorder}`,
            }}
          />
        ) : (
          <Box
            sx={{
              width: logoSize,
              height: logoSize,
              borderRadius: "50%",
              backgroundColor: dt.blueSoft,
              border: `2px solid ${dt.blueGlow}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box sx={{ fontFamily: dt.font, fontSize: isPhone ? "13px" : "16px", fontWeight: 700, color: dt.blue }}>
              {provider.name?.charAt(0)?.toUpperCase() || "?"}
            </Box>
          </Box>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            component={RouterLink}
            to={`/companies/${provider.id}`}
            sx={{
              fontFamily: dt.font,
              fontSize: isPhone ? "14px" : "16px",
              fontWeight: 700,
              color: dt.textLink,
              textDecoration: "none",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {provider.name}
          </Box>
        </Box>
      </Box>

      {/* Location */}
      {locationText && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <LocationOnOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />
          <Box sx={{ fontSize: "13px", fontFamily: dt.font, fontWeight: 500, color: dt.textSecondary }}>
            {locationText}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OfferProviderCard;
